import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, IconButton, Tooltip } from "@mui/material";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { isAllowedEmbedDomain } from "../../../utilities/embedUtils";
import { useMediaActions } from "../../../store/media";

interface EmbedRendererProps {
  embedUrl: string;
  title?: string;
  mode?: "preview" | "thumbnail" | "dialog" | "expanded";
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: (error?: any) => void;
  onClick?: () => void;
  onClose?: () => void;
  onFullscreen?: () => void;
  isFullscreen?: boolean;
}

export const EmbedRenderer: React.FC<EmbedRendererProps> = React.memo(
  ({
    embedUrl,
    title,
    mode = "preview",
    width = "100%",
    height = "100%",
    onLoad,
    onError,
    onClick,
    onClose,
    onFullscreen,
    isFullscreen = false
  }) => {
    const { closeMediaDetail } = useMediaActions();
    // Use prop if provided (dialog mode), otherwise fall back to the media store
    // action so the pill's close button works in expanded/carousel mode too.
    const handleClose = onClose ?? closeMediaDetail;
    const isAllowed = isAllowedEmbedDomain(embedUrl);
    // Only show loading state when we'll actually render an iframe
    const [isLoading, setIsLoading] = useState(isAllowed);
    const [controlsExpanded, setControlsExpanded] = useState(true);

    // Signal "ready" to the parent as soon as the embed surface is mounted, regardless of
    // whether the third-party iframe has fired its `load` event yet. Many providers (e.g.
    // Polycam, Sketchfab) either delay the iframe `load` event or have it suppressed by
    // X-Frame-Options/CSP, which would otherwise leave the parent's overlay stuck on top
    // of the embed indefinitely. EmbedRenderer keeps its own internal `isLoading` for the
    // iframe content; the parent overlay can safely disappear.
    useEffect(() => {
      onLoad?.();
    }, [onLoad, isAllowed, embedUrl]);

    // Safety timeout: clear our internal loading state if the iframe never fires `load`.
    // The iframe still renders whatever it managed to render (or an X-Frame-Options error
    // page), and we stop showing a spinner over it forever.
    useEffect(() => {
      if (!isLoading) return;
      const timer = window.setTimeout(() => setIsLoading(false), 8000);
      return () => window.clearTimeout(timer);
    }, [isLoading, embedUrl]);

    if (!embedUrl) {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: 1
          }}
        >
          <Typography color='text.secondary'>No embed URL provided</Typography>
        </Box>
      );
    }

    if (mode === "thumbnail") {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: onClick ? "pointer" : "default",
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: 1,
            gap: 0.5
          }}
          onClick={onClick}
        >
          <ViewInArIcon sx={{ fontSize: 32, color: "text.secondary" }} />
          <Typography variant='caption' color='text.secondary'>
            3D / Embed
          </Typography>
        </Box>
      );
    }

    if (!isAllowed) {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 3,
            textAlign: "center"
          }}
        >
          <BlockIcon sx={{ fontSize: 48, color: "error.main" }} />
          <Typography variant='body1' color='error.main'>
            Embed blocked: untrusted domain
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 400 }}>
            The domain for this embed is not in the allowed list. Contact an administrator to add it.
          </Typography>
          <Box
            component='a'
            href={embedUrl}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: "primary.main",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" }
            }}
          >
            <OpenInNewIcon fontSize='small' />
            <Typography variant='body2'>Open in new tab instead</Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width,
          height: height,
          minHeight: 280,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#1a1a2e"
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              zIndex: 1
            }}
          >
            <CircularProgress size={32} sx={{ color: "grey.400" }} />
            <Typography variant='body2' color='grey.400'>
              Loading embedded content...
            </Typography>
          </Box>
        )}

        {/* Collapsible control pill — our own fullscreen/close buttons clearly
            separated from the embed provider's own chrome. Open by default;
            collapses to a small handle so the user can recover it. */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            background: "rgba(0,0,0,0.68)",
            backdropFilter: "blur(8px)",
            borderRadius: "2rem",
            padding: "0.2rem 0.35rem",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
            transition: "all 0.2s ease",
            "& .MuiIconButton-root": {
              color: "rgba(255,255,255,0.85)",
              width: 32,
              height: 32,
              "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.15)" }
            }
          }}
        >
          {controlsExpanded && (
            <>
              {onFullscreen && (
                <Tooltip title={isFullscreen ? "Exit full screen" : "Full screen"}>
                  <IconButton size='small' onClick={onFullscreen}>
                    {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: "1.2rem" }} /> : <FullscreenIcon sx={{ fontSize: "1.2rem" }} />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title='Close'>
                <IconButton size='small' onClick={handleClose}>
                  <CloseIcon sx={{ fontSize: "1.2rem" }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title={controlsExpanded ? "Hide controls" : "Show controls"}>
            <IconButton size='small' onClick={() => setControlsExpanded((v) => !v)} sx={{ opacity: 0.7 }}>
              {controlsExpanded ? <ChevronRightIcon sx={{ fontSize: "1rem" }} /> : <ChevronLeftIcon sx={{ fontSize: "1rem" }} />}
            </IconButton>
          </Tooltip>
        </Box>

        {/*
          Note: we intentionally do NOT set the `sandbox` attribute here.
          The domain allowlist (`isAllowedEmbedDomain`) is the real security
          boundary; layering a restrictive sandbox on top of it broke a number
          of trusted providers (Polycam, Sketchfab) which rely on cookies,
          pointer-lock, full WebGL/WebXR, etc. Any new provider must be
          explicitly added to ALLOWED_EMBED_DOMAINS.
        */}
        <iframe
          src={embedUrl}
          title={title || "Embedded content"}
          allow='accelerometer; gyroscope; xr-spatial-tracking; fullscreen; clipboard-write'
          style={{
            width: "100%",
            height: "100%",
            minHeight: 280,
            maxHeight: mode === "expanded" ? "100%" : 720,
            border: "none",
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.3s ease"
          }}
          onLoad={() => {
            setIsLoading(false);
            onLoad?.();
          }}
          onError={(e) => {
            setIsLoading(false);
            onError?.(e);
          }}
        />
      </Box>
    );
  }
);

export default EmbedRenderer;
